import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { normalizeErrorMessage } from '@/utils/normalizeError';

const AuthPage = ({ isModal = false, initialMode = 'login', onClose = () => {}, onForgotPassword = null, onSwitchMode = null }) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invitedEmail = searchParams.get('email');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: invitedEmail || '',
    password: '',
    confirmPassword: '',
    rememberMe: false
  });

  // Sync isLogin with initialMode prop (URL-driven)
  useEffect(() => {
    setIsLogin(initialMode === 'login');
  }, [initialMode]);
  
  // If invited, switch to signup mode
  useEffect(() => {
    if (invitedEmail) {
      setIsLogin(false);
      setFormData(prev => ({ ...prev, email: invitedEmail }));
    }
  }, [invitedEmail]);

  const handleToggleMode = () => {
    const newMode = isLogin ? 'signup' : 'login';
    if (onSwitchMode) {
      onSwitchMode(newMode);
    } else {
      setIsLogin(!isLogin);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isLogin && formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/signup';
      const payload = isLogin
        ? { email: formData.email, password: formData.password, rememberMe: formData.rememberMe }
        : { firstName: formData.firstName, lastName: formData.lastName, email: formData.email, password: formData.password };

      const response = await axios.post(`${API}${endpoint}`, payload);
      login(response.data.token, response.data.user, navigate, isLogin ? formData.rememberMe : false);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      
      // Close modal if in modal mode
      if (isModal && onClose) {
        onClose();
      }
    } catch (error) {
      toast.error(normalizeErrorMessage(error, 'Authentication failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={isModal ? "p-6" : "min-h-screen flex items-center justify-center p-4"} style={!isModal ? { background: '#FF6600' } : {}}>
      <Card className="w-full max-w-md" data-testid="auth-card">
        <CardHeader className="space-y-1">
          <CardTitle className="text-3xl font-bold text-center">Specard</CardTitle>
          <CardDescription className="text-center">
            {isLogin ? 'Sign in to your account' : 'Create a new account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      data-testid="firstname-input"
                      type="text"
                      placeholder="John"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      data-testid="lastname-input"
                      type="text"
                      placeholder="Doe"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                    />
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                readOnly={!!invitedEmail}
                className={invitedEmail ? 'bg-slate-100 cursor-not-allowed' : ''}
              />
              {invitedEmail && (
                <p className="text-xs text-blue-600">
                  This email was shared with a project. Use this email to sign up.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                data-testid="password-input"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>

            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <PasswordInput
                  id="confirmPassword"
                  data-testid="confirm-password-input"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                />
                {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-sm text-red-500" data-testid="password-mismatch-error">Passwords do not match</p>
                )}
              </div>
            )}

            {isLogin && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="rememberMe"
                  data-testid="remember-me-checkbox"
                  checked={formData.rememberMe}
                  onCheckedChange={(checked) => setFormData({ ...formData, rememberMe: !!checked })}
                />
                <label htmlFor="rememberMe" className="text-sm text-gray-600 cursor-pointer select-none">
                  Remember me
                </label>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading || (!isLogin && formData.password !== formData.confirmPassword)}
              data-testid="auth-submit-button"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Sign Up')}
            </Button>
          </form>

          <div className="mt-4 text-center text-sm">
            <button
              type="button"
              onClick={handleToggleMode}
              className="text-primary hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
            {isLogin && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={onForgotPassword ? onForgotPassword : () => window.location.href = '/'}
                  className="text-sm text-gray-500 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthPage;